import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { MODULE_SCHEMAS, MODULE_ORDER } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Timer, CheckCircle2, X, Heart } from "lucide-react";

export default function EmergencyAccess() {
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [checkin, setCheckin] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    const [c, r, ci] = await Promise.all([
      api.get("/emergency/contacts"),
      api.get("/emergency/requests"),
      api.get("/emergency/checkin/status"),
    ]);
    setContacts(c.data);
    setRequests(r.data);
    setCheckin(ci.data);
  };

  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (dialog.item?.id) {
      await api.put(`/emergency/contacts/${dialog.item.id}`, payload);
      toast.success("Updated");
    } else {
      await api.post("/emergency/contacts", payload);
      toast.success("Added");
    }
    setDialog({ open: false, item: null });
    await load();
  };

  const remove = async (id) => {
    if (!confirm("Remove this emergency contact?")) return;
    await api.delete(`/emergency/contacts/${id}`);
    toast.success("Removed");
    await load();
  };

  const requestAccess = async (contact_id) => {
    const reason = prompt("Reason for emergency access request?");
    if (!reason) return;
    await api.post("/emergency/requests", { contact_id, reason });
    toast.success("Request created (pending approval)");
    await load();
  };

  const doCheckin = async () => {
    await api.post("/emergency/checkin");
    toast.success("Check-in recorded");
    await load();
  };

  const approve = async (id) => {
    await api.post(`/emergency/requests/${id}/approve`);
    toast.success("Approved — waiting period starts");
    await load();
  };
  const reject = async (id) => {
    await api.post(`/emergency/requests/${id}/reject`);
    toast.success("Rejected");
    await load();
  };

  return (
    <div className="space-y-6" data-testid="emergency-access-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">Controlled Release</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Emergency Access
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Nominate trusted people, define what they can see, and require a waiting period
            before access is released. Every attempt is logged.
          </p>
        </div>
        <Button onClick={() => setDialog({ open: true, item: null })}
          data-testid="ea-add-contact-button">
          <Plus className="h-4 w-4 mr-2" /> Add contact
        </Button>
      </div>

      <Card className="p-6 bg-slate-50 border-slate-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="label-overline">Dead-man Switch</div>
            <div className="font-heading text-lg font-semibold mt-1">Periodic check-in</div>
            <div className="text-sm text-slate-600 mt-1">
              Last check-in:{" "}
              <span className="font-medium tabular-nums">
                {checkin?.last_checkin ? formatDate(checkin.last_checkin) : "never"}
              </span>
            </div>
          </div>
          <Button variant="outline" onClick={doCheckin} data-testid="ea-checkin-button">
            <Heart className="h-4 w-4 mr-2" /> I'm okay — check in
          </Button>
        </div>
      </Card>

      {/* Contacts */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="font-heading font-semibold">Emergency Contacts</div>
        </div>
        <div className="divide-y divide-slate-100">
          {contacts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No emergency contacts yet.
            </div>
          ) : (
            contacts.map((c) => (
              <div key={c.id} className="p-4 flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{c.full_name}</div>
                  <div className="text-xs text-slate-500">{c.email} · {c.relationship}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-[10px] capitalize">{c.role}</Badge>
                    <Badge variant="outline" className="text-[10px]">
                      <Timer className="h-3 w-3 mr-1" /> {c.waiting_period_hours}h wait
                    </Badge>
                    {c.modules_allowed?.length === 0 ? (
                      <Badge className="text-[10px] bg-slate-900">Full access</Badge>
                    ) : (
                      c.modules_allowed.map((m) => (
                        <Badge key={m} variant="secondary" className="text-[10px]">
                          {MODULE_SCHEMAS[m]?.label || m}
                        </Badge>
                      ))
                    )}
                  </div>
                  {c.notes && <div className="text-xs text-slate-500 mt-2">{c.notes}</div>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => requestAccess(c.id)}
                    data-testid={`ea-request-${c.id}`}>
                    Request access
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => setDialog({ open: true, item: c })}
                    data-testid={`ea-edit-${c.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)}
                    data-testid={`ea-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Requests */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="font-heading font-semibold">Access Requests</div>
        </div>
        <div className="divide-y divide-slate-100">
          {requests.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No requests yet.</div>
          ) : (
            requests.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{r.contact_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.reason} · requested {formatDate(r.requested_at)}
                  </div>
                </div>
                <Badge variant={
                  r.status === "approved" ? "default"
                  : r.status === "rejected" ? "destructive"
                  : "secondary"
                } className="capitalize">
                  {r.status}
                </Badge>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve(r.id)} data-testid={`ea-approve-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(r.id)}
                      data-testid={`ea-reject-${r.id}`}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
                {r.release_at && (
                  <div className="text-xs text-slate-500">Release at {formatDate(r.release_at)}</div>
                )}
              </div>
            ))
          )}
        </div>
      </Card>

      <ContactDialog dialog={dialog} onClose={() => setDialog({ open: false, item: null })} onSave={save} />
    </div>
  );
}

function ContactDialog({ dialog, onClose, onSave }) {
  const [form, setForm] = useState({
    full_name: "", email: "", relationship: "", role: "viewer",
    modules_allowed: [], waiting_period_hours: 48, notes: "",
  });
  useEffect(() => {
    if (dialog.item) {
      setForm({
        full_name: dialog.item.full_name, email: dialog.item.email,
        relationship: dialog.item.relationship, role: dialog.item.role,
        modules_allowed: dialog.item.modules_allowed || [],
        waiting_period_hours: dialog.item.waiting_period_hours,
        notes: dialog.item.notes || "",
      });
    } else {
      setForm({
        full_name: "", email: "", relationship: "", role: "viewer",
        modules_allowed: [], waiting_period_hours: 48, notes: "",
      });
    }
  }, [dialog]);

  const toggleModule = (slug) => {
    setForm((f) => ({
      ...f,
      modules_allowed: f.modules_allowed.includes(slug)
        ? f.modules_allowed.filter((x) => x !== slug)
        : [...f.modules_allowed, slug],
    }));
  };

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialog.item ? "Edit contact" : "Add emergency contact"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="ea-form-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="ea-form-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Relationship</Label>
              <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                data-testid="ea-form-relation" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="ea-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["owner", "spouse", "dependent", "executor", "advisor", "viewer"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Waiting Period (hours)</Label>
            <Input type="number" value={form.waiting_period_hours}
              onChange={(e) => setForm({ ...form, waiting_period_hours: Number(e.target.value) })}
              data-testid="ea-form-wait" />
          </div>
          <div>
            <Label>Allowed Modules (leave empty = full access)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto p-2 border border-slate-200 rounded-md">
              {MODULE_ORDER.map((slug) => (
                <label key={slug} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.modules_allowed.includes(slug)}
                    onCheckedChange={() => toggleModule(slug)}
                  />
                  {MODULE_SCHEMAS[slug].label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="ea-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} data-testid="ea-form-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
